import { GeoWebServiceClient } from './GeoWebServiceClient'

/**
 * Interface for representing a 2D bounding box on the map.
 */
export interface GeoModelBoundingBox {
  MinX: number
  MinY: number
  MaxX: number
  MaxY: number
  Srid: number
}

/**
 * Interface for the objects that come out of the 'geomodel'
 * webservice on Geo's servers.
 */
export interface GeoModel {
  ID: string
  Name: string
  Description: string
  BoundingBox: GeoModelBoundingBox
  Type: number
  Priority: number
  Geometry: GeoGeometry
}

/**
 * Interface for the geometry part of the Geo models.
 */
export interface GeoGeometry {
  type: string
  coordinates: ol.Coordinate[][]
}

/**
 * Web service client for fetching the model areas within Geo's
 * system (used for profile tool, virtual boring, and the slice layers).
 */
export class GeoModelService extends GeoWebServiceClient {
  static get URL (): string { return 'https://data.geo.dk/api/v3/geomodel' }

  /**
   * Constructor.
   * @param token The Geo auth token.
   */
  constructor (token: string) {
    super(GeoModelService.URL, token, { format: 'geojson' })
  }

  /**
   * [Required] Setter for the x coordinate.
   * @param x The x coordinate.
   */
  setX (x: number): void {
    this.addParam('x', x.toString())
  }

  /**
   * [Required] Setter for the y coordinate.
   * @param y The y coordinate.
   */
  setY (y: number): void {
    this.addParam('y', y.toString())
  }

  /**
   * Validates that required params are given.
   * In this case that means that we check that either none of
   * x and y are set, or that they both are set.
   */
  private validateParams (): void {
    const hasX = this.hasParam('x')
    const hasY = this.hasParam('y')
    if ((hasX || hasY) && !(hasX && hasY)) {
      throw new Error('Parameter mismatch: Both x and y are needed.')
    }
  }

  /**
   * For some reason, the Geo webservice returns the models as an escaped
   * GeoJSON object, i.e. it has been placed within a string and all
   * "-characters have been escaped. This method unwraps such objects.
   * @param stringRepr The escaped string representation of a JSON object.
   * @returns The unpacked JSON object.
   */
  private parseGeojsonString (stringRepr: string): any {
    stringRepr = stringRepr.replaceAll('\\"', '"') // Unescape the " character.
    stringRepr = stringRepr.substring(1, stringRepr.length - 1) // Remove the leading and trailing " character.
    return JSON.parse(stringRepr) // And finally parse the JSON inside.
  }

  /**
   * Calls the web service.
   * @returns A promise bearing the model area objects.
   * @throws An Error if an invalid response object is returned.
   */
  async fetchModels (): Promise<GeoModel[]> {
    this.validateParams()

    // Fetch the full GEOJSON model list.
    const response: Response = await fetch(this.url(), { headers: { Authorization: `Bearer ${this.token}` } })
    if (!response.ok) {
      throw new Error('Got invalid HTTP response.')
    }

    const responseData = await response.text()
    const jsonResponse = this.parseGeojsonString(responseData)
    const models: GeoModel[] = []
    jsonResponse.features.forEach((feature: any) => {
      models.push({
        ID: feature.properties.Id,
        Name: feature.properties.Name,
        Description: feature.properties.Description,
        BoundingBox: {
          MinX: feature.properties.BoundingBox.MinX,
          MinY: feature.properties.BoundingBox.MinY,
          MaxX: feature.properties.BoundingBox.MaxX,
          MaxY: feature.properties.BoundingBox.MaxY,
          Srid: feature.properties.BoundingBox.Srid
        },
        Type: feature.properties.Type,
        Priority: feature.properties.Priority,
        Geometry: feature.geometry
      })
    })

    return models
  }
}
